/**
 * IndexedDB utility for storing upload sessions
 * Uses Dexie.js for easier IndexedDB management
 */

import Dexie from 'dexie';

// Create database instance
const db = new Dexie('UploaderDB');

// Define database schema
db.version(1).stores({
  sessions: 'sessionId, fileName, projectId, status, expiresAt, uploadedBytes, serverSessionId'
});

// Export database instance
export { db };

/**
 * Save upload session to IndexedDB
 * @param {Object} session - Upload session object
 */
export async function saveUploadSession(session) {
  try {
    await db.sessions.put(session);
  } catch (error) {
    console.error('Error saving upload session to IndexedDB:', error);
    throw error;
  }
}

/**
 * Get upload session by ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object|null>} Upload session or null if not found
 */
export async function getUploadSession(sessionId) {
  try {
    return await db.sessions.get(sessionId) || null;
  } catch (error) {
    console.error('Error getting upload session from IndexedDB:', error);
    return null;
  }
}

/**
 * Get all upload sessions
 * @returns {Promise<Array>} Array of upload sessions
 */
export async function getAllUploadSessions() {
  try {
    return await db.sessions.toArray();
  } catch (error) {
    console.error('Error getting all upload sessions from IndexedDB:', error);
    return [];
  }
}

/**
 * Get sessions by project ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} Array of upload sessions for the project
 */
export async function getSessionsByProject(projectId) {
  try {
    return await db.sessions.where('projectId').equals(projectId).toArray();
  } catch (error) {
    console.error('Error getting sessions by project from IndexedDB:', error);
    return [];
  }
}

/**
 * Get active sessions (not completed/cancelled)
 * @returns {Promise<Array>} Array of active upload sessions
 */
export async function getActiveSessions() {
  try {
    return await db.sessions
      .where('status')
      .anyOf(['ready', 'initializing', 'uploading', 'paused', 'error'])
      .toArray();
  } catch (error) {
    console.error('Error getting active sessions from IndexedDB:', error);
    return [];
  }
}

/**
 * Delete upload session
 * @param {string} sessionId - Session ID to delete
 */
export async function deleteUploadSession(sessionId) {
  try {
    await db.sessions.delete(sessionId);
  } catch (error) {
    console.error('Error deleting upload session from IndexedDB:', error);
    throw error;
  }
}

/**
 * Clear all sessions
 */
export async function clearAllSessions() {
  try {
    await db.sessions.clear();
  } catch (error) {
    console.error('Error clearing all sessions from IndexedDB:', error);
    throw error;
  }
}

/**
 * Clean up expired sessions
 */
export async function cleanupExpiredSessions() {
  try {
    const now = Date.now();
    await db.sessions
      .where('expiresAt')
      .below(now)
      .delete();
  } catch (error) {
    console.error('Error cleaning up expired sessions from IndexedDB:', error);
  }
}

/**
 * Get storage usage estimate
 * @returns {Promise<Object|null>} Storage usage info or null if not available
 */
export async function getStorageUsage() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage,
        quota: estimate.quota,
        percentUsed: estimate.quota ? (estimate.usage / estimate.quota) * 100 : 0
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting storage usage:', error);
    return null;
  }
}

/**
 * Migrate data from localStorage to IndexedDB
 * Called once on app initialization
 */
export async function migrateFromLocalStorage() {
  try {
    // Check if migration already done
    const migrationDone = localStorage.getItem('indexedDB_migration_done');
    if (migrationDone === 'true') {
      return; // Already migrated
    }

    // Get sessions from localStorage
    const stored = localStorage.getItem('uploadSessions');
    if (!stored) {
      // No data to migrate
      localStorage.setItem('indexedDB_migration_done', 'true');
      return;
    }

    const sessions = JSON.parse(stored);
    if (!Array.isArray(sessions) || sessions.length === 0) {
      // No sessions to migrate
      localStorage.setItem('indexedDB_migration_done', 'true');
      return;
    }

    // Migrate sessions to IndexedDB
    console.log(`Migrating ${sessions.length} sessions from localStorage to IndexedDB...`);
    await db.sessions.bulkPut(sessions);

    // Mark migration as done
    localStorage.setItem('indexedDB_migration_done', 'true');
    
    // Optionally clear localStorage (keep for now in case of rollback)
    // localStorage.removeItem('uploadSessions');
    
    console.log('Migration completed successfully');
  } catch (error) {
    console.error('Error migrating from localStorage to IndexedDB:', error);
    // Don't throw - allow app to continue even if migration fails
  }
}
